import { appLogger } from '@tn/foundation/utils/app-logger/appLogger';
import { createConsoleLogger } from '@tn/foundation/utils/devtools/console-logger';
/**
 * Client manifest/CSS base URLs: `getDomain` and static `/static/{app}` prefix must follow the same
 * runtime deployment env as federation (`getClientRuntimeCurrentEnv`), not only `process.env` in the browser.
 */
import { getClientRuntimeCurrentEnv, getDomain, MfeNames } from '@tn/foundation/utils/domains';
import { isDevEnv } from '@tn/foundation/utils/getCurrentEnv';

const logger = createConsoleLogger('getRemotePageCSSAssets');

interface Manifest {
    exposes: Array<{
        name: string;
        assets?: {
            css?: {
                sync?: string[];
            };
        };
    }>;
}

/** Result of fetching remote page CSS assets: base URL (client domain + env prefix) and CSS file names. */
export interface RemotePageCSSAssetsResult {
    /** Base URL for CSS assets (client domain + env prefix, e.g. http://example.com or http://example.com/static/app-shell). */
    baseUrl: string;
    /** CSS file names from the manifest (e.g. ['file1.css', 'file2.css']). */
    cssFiles: string[];
}

interface ParsedRemoteModule {
    appName: string;
    remoteModulePath: string;
}

function parseRemoteModule(
    remoteModule: `${MfeNames}/${string}` | string,
): ParsedRemoteModule | null {
    if (!remoteModule) {
        logger.log('early return: remoteModule is empty');
        return null;
    }
    const [appName, ...modulePath] = remoteModule.split('/');
    const remoteModulePath = modulePath.join('/');
    logger.log(
        'parsed appName:',
        JSON.stringify(appName),
        'remoteModulePath:',
        JSON.stringify(remoteModulePath),
    );
    if (!appName || !remoteModulePath) {
        logger.log('early return: missing appName or remoteModulePath');
        return null;
    }
    return { appName, remoteModulePath };
}

async function fetchManifest(manifestUrl: string): Promise<Manifest | null> {
    try {
        const manifestResponse = await fetch(manifestUrl);
        if (!manifestResponse.ok) {
            appLogger.error('[getRemotePageCSSAssets] failed to fetch manifest', {
                status: manifestResponse.status,
                statusText: manifestResponse.statusText,
                url: manifestUrl,
            });
            return null;
        }
        const manifest = await manifestResponse.json();
        logger.log('manifest fetched ok, exposes count:', manifest?.exposes?.length ?? 0);
        return manifest;
    } catch (error) {
        appLogger.error(
            '[getRemotePageCSSAssets] error fetching or parsing manifest',
            undefined,
            error,
        );
        return null;
    }
}

function getCssFilesFromManifest(manifest: Manifest, remoteModulePath: string): string[] | null {
    const entry = manifest.exposes?.find((mf) => mf.name === remoteModulePath);
    if (!entry) {
        const names = manifest.exposes?.map((e) => e.name).slice(0, 10) ?? [];
        logger.log(
            'no manifest entry for remoteModulePath=',
            JSON.stringify(remoteModulePath),
            'sample expose names:',
            JSON.stringify(names),
        );
        return null;
    }
    const cssFiles = entry.assets?.css?.sync;
    if (!cssFiles?.length) {
        logger.log(
            'entry found but no CSS assets (assets?.css?.sync):',
            JSON.stringify(entry?.assets),
        );
        return null;
    }
    return cssFiles;
}

function resolveEnvForStaticAssetPrefix(): string {
    if (typeof window === 'undefined') {
        return process.env.CURRENT_ENV || 'prod';
    }
    return getClientRuntimeCurrentEnv();
}

function buildBaseUrl(appName: string): string {
    // Use isDevEnv() (runtime-safe): on the client, process.env.CURRENT_ENV is not reliably set
    // for reusable Docker images — the runtime env lives on window.__CURRENT_ENV__ (seeded in
    // _app.tsx from getInitialProps). Reading process.env directly would misdetect platypus
    // feature instances as "higher env" and emit /static/{app} paths that don't exist on
    // shared.branch FIs.
    if (!isDevEnv()) {
        // Higher-env client: relative path so <link href=…> resolves against the current origin,
        // which avoids hardcoding the public apex (e.g. pluto.tv) into bundles that also run on
        // pre-prod hosts like www-prod.pluto.tv before CNAME cutover.
        return `/static/${appName}`;
    }
    const clientDomain = getDomain(appName as MfeNames, '', false);
    const deployEnv = resolveEnvForStaticAssetPrefix();
    const isHigherEnv = !['local', 'platypus'].includes(deployEnv);
    const prefix = isHigherEnv ? `/static/${appName}` : '';
    return `${clientDomain}${prefix}`;
}

/**
 * Fetches the remote module federation manifest for a given app shell URL,
 * extracts the CSS asset filenames for the mapped remote module, and returns baseUrl + cssFiles.
 *
 * @param remoteModule - The identifier of the remote module, should be in the format of {@link MfeNames}/remote-module-name
 * @returns A Promise that resolves to {@link RemotePageCSSAssetsResult}, or null if no assets are found.
 */
export const getRemotePageCSSAssets = async (
    remoteModule: `${MfeNames}/${string}` | string = '',
): Promise<RemotePageCSSAssetsResult | null> => {
    logger.log('called with remoteModule:', JSON.stringify(remoteModule));

    const parsed = parseRemoteModule(remoteModule);
    if (!parsed) return null;

    const isServer = typeof window === 'undefined';
    const manifestBaseUrl = isServer
        ? getDomain(parsed.appName as MfeNames, '', true)
        : buildBaseUrl(parsed.appName);
    const manifestUrl = `${manifestBaseUrl}/_next/static/chunks/mf-manifest.json`;
    logger.log('manifestBaseUrl:', manifestBaseUrl, 'fetching manifest:', manifestUrl);

    const manifest = await fetchManifest(manifestUrl);
    if (!manifest) return null;

    const cssFiles = getCssFilesFromManifest(manifest, parsed.remoteModulePath);
    if (!cssFiles) return null;

    const baseUrl = buildBaseUrl(parsed.appName);
    logger.log('returning', cssFiles.length, 'CSS file(s), baseUrl:', baseUrl);
    return { baseUrl, cssFiles };
};

/**
 * The CSS of several exposes of one remote, for a page that renders one of them per
 * request: one list, duplicates dropped, on the first base URL found.
 */
export const mergeRemotePageCSSAssets = (
    results: ReadonlyArray<RemotePageCSSAssetsResult | null>,
): RemotePageCSSAssetsResult | null => {
    const found = results.filter((result): result is RemotePageCSSAssetsResult => !!result);
    if (!found.length) return null;
    return {
        baseUrl: found[0].baseUrl,
        cssFiles: [...new Set(found.flatMap((result) => result.cssFiles))],
    };
};

/** `getRemotePageCSSAssets` over one expose or several, merged. */
export const getRemotePagesCSSAssets = async (
    remoteModules: string | readonly string[],
): Promise<RemotePageCSSAssetsResult | null> => {
    const list = typeof remoteModules === 'string' ? [remoteModules] : remoteModules;
    return mergeRemotePageCSSAssets(
        await Promise.all(list.map((remoteModule) => getRemotePageCSSAssets(remoteModule))),
    );
};

/**
 * Creates an array of <link> elements for preloading CSS files.
 * Builds full URLs from {@link RemotePageCSSAssetsResult.baseUrl} and {@link RemotePageCSSAssetsResult.cssFiles}.
 *
 * @param result - {@link RemotePageCSSAssetsResult} from {@link getRemotePageCSSAssets}, or null
 * @returns An array of JSX <link> elements for preloading the CSS files, or null if no assets are provided.
 */
export const createCSSPreloadLinks = (result: RemotePageCSSAssetsResult | null) => {
    if (!result?.cssFiles?.length) {
        return null;
    }
    const urls = result.cssFiles.map((cssFile: string) => `${result.baseUrl}/_next/${cssFile}`);
    return urls.flatMap((url: string) => [
        <link key={url + '_preload'} rel="preload" href={url} as="style" />,
        <link key={url + '_style'} rel="stylesheet" href={url} data-n-g="" />,
    ]);
};
